// shared-lib/fixture-types.js
// Single source of truth for deriving an object's type string from its data.
// Pure functions, no SDK deps. Imported by both web-app and cloud functions.

// Type values that don't actually identify the object — they mean "this is a
// 3D object loaded from a GLB" without saying what kind. When we see one of
// these on `obj.type`, fall through to other fields.
const GENERIC_TYPES = ['glb-object', 'object'];

/**
 * Derive a normalized lowercase type string for an object.
 *
 * Priority order:
 *   1. obj.type (when present and not in GENERIC_TYPES)
 *   2. First key of obj.category (when category is an object) — with
 *      'fireplace' and 'countertop' preferred over alphabetical first key
 *      to match historical web-app behavior.
 *   3. obj.categoryName (legacy RoomPlan field, pre-2025 scan data)
 *   4. obj.category as a string (legacy shape)
 *
 * Returns null for null/undefined input, 'unknown' when nothing matches.
 */
function getObjectType(obj) {
  if (!obj) return null;

  if (
    obj.type &&
    typeof obj.type === 'string' &&
    !GENERIC_TYPES.includes(obj.type.toLowerCase())
  ) {
    return obj.type.toLowerCase();
  }

  if (typeof obj.category === 'object' && obj.category !== null) {
    if (obj.category.fireplace) return 'fireplace';
    if (obj.category.countertop) return 'countertop';
    const keys = Object.keys(obj.category);
    if (keys.length > 0) return keys[0].toLowerCase();
  }

  if (obj.categoryName && typeof obj.categoryName === 'string') {
    return obj.categoryName.toLowerCase();
  }

  if (typeof obj.category === 'string') return obj.category.toLowerCase();

  return 'unknown';
}

/**
 * The SCAN's own word for an object, after the pipeline may have renamed it.
 *
 * `getObjectType` answers "what IS this" — the pipeline's current verdict.
 * This answers "what did the scanner CALL it", which is a different question
 * and the one an identity check has to ask: `dropped-vanity-check` puts the
 * scanner's label to the model and asks whether it fits, so asking with our
 * own renamed word would test a claim the scan never made.
 *
 * Read in three steps, one per cohort of data:
 *   1. `scannedLabel` — stamped at the rename itself (`cleanCabinetsCore`,
 *      `cleanStorageFixtures`), so a box renamed from now on carries its
 *      origin explicitly.
 *   2. the first `category` key — how it survived before there was a field
 *      for it, and still how the ~142 boxes already renamed in Firestore
 *      carry it. Those read `{ type: 'lower-cabinet', category: { storage:
 *      true } }`; the category key is the scanned one.
 *   3. `getObjectType` — an untouched box, where all three agree anyway.
 *
 * Step 1 exists because step 2 was never a place to keep provenance: the
 * rename now rewrites `category` too (it has to — an object claiming
 * `type: 'lower-cabinet'` and `category: { storage: true }` draws as a
 * cabinet in 3D and a dresser in every elevation), which would have taken
 * the scanner's word with it.
 */
function scannedLabelOf(obj) {
  if (obj && typeof obj.scannedLabel === 'string' && obj.scannedLabel) {
    return obj.scannedLabel;
  }
  const category =
    obj && typeof obj.category === 'object' && obj.category !== null
      ? Object.keys(obj.category)[0]
      : null;
  return category || getObjectType(obj);
}

module.exports = {
  GENERIC_TYPES,
  getObjectType,
  scannedLabelOf,
};
